
import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useIsMobile } from '@uplift/hooks';
import { ProviderMedicalRole } from '@uplift/types';
import { Button } from '@uplift/ui-v2';

import {
  StepContainer, StepContent, StepDescription, StepHeading, StepNavigator
} from '../../components';
import { useBetterhelpReferralDetails } from '../useBetterhelpUserDetails.hook';

export type WelcomeProps = {
  onNext: () => void;
  inviteToken: string;
  medicalRole: ProviderMedicalRole;
};

const BookingStep: React.VFC<{ number: number; title: string }> = (props) => {
  const { classes } = useStyles();

  return (
    <Box key={props.number} className={classes.steps}>
      <Typography variant="body1" className={classes.stepNumber} data-testid={`step-number-${props.number}`}>{props.number}</Typography>
      <Typography variant="body1">{props.title}</Typography>
    </Box>
  );
};

export const Welcome: React.VFC<WelcomeProps> = props => {
  const isMobile = useIsMobile();
  const { classes } = useStyles();
  const betterhelpUserData = useBetterhelpReferralDetails(props.inviteToken);

  const actions = (
    <Button variant="contained" color="secondary" className={classes.getStarted} onClick={props.onNext} data-testid="betterhelp-get-started-btn">Get Started</Button>
  );

  return (
    <StepContainer>
      <div className={classes.container}>
        <div className={classes.imageContainer}>
          <img
            className={classes.image}
            src="https://scdn.joinuplift.co/public/application-assets/student-onboarding-welcome.png"
            alt="Welcome to UpLift!"
          />
        </div>
        <StepContent className={classes.content}>
          <Box mt={{ md: 'auto' }} mb={{ xs: 3, md: 0 }}>
            <StepHeading variant="h1">
              We’re here for you,&nbsp;
              <Typography component="span" color="secondary" variant="inherit" data-testid="welcome-betterhelp-name">{betterhelpUserData?.firstName}</Typography>
            </StepHeading>
            <Box display="flex" flexDirection="column" gap="24px">
              <StepDescription variant="h4" data-testid="uplift-betterhelp-partnership">
                In partnership with {betterhelpUserData.eapName} you have access to mental health services through Betterhelp.
              </StepDescription>
              <Box className={classes.bookSession}>
                <Typography variant="overline" className={classes.bookSessionTitle} data-testid="book-session-heading">
                  Book your psychiatry session
                </Typography>
                <BookingStep number={1} title="Create your account" />
                <BookingStep number={2} title="Tell us what you want to discuss" />
                <BookingStep number={3} title="Select a time that’s convenient for you" />
              </Box>
            </Box>
            {!isMobile && actions}
          </Box>
          <div className={classes.preventionContainer}>
            <Typography variant="body2">If you are considering suicide or if you or any other person may be in danger, please call or text 988 (24-hour suicide and crisis lifeline) or call 911.</Typography>
          </div>
        </StepContent>
        {isMobile && <StepNavigator>{actions}</StepNavigator>}
      </div>
    </StepContainer>
  );
};

const useStyles = makeStyles({ name: 'Welcome' })(theme => ({
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',

    [theme.breakpoints.up('md')]: {
      display: 'grid',
      gridTemplateColumns: '550px 1fr',
    }
  },
  imageContainer: {
    display: 'flex',
    height: '370px',
    [theme.breakpoints.up('md')]: {
      height: '100%',
    }
  },
  image: {
    objectFit: 'cover',
    width: '100%',
    height: '100%',
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    maxWidth: `calc(497px + ${theme.spacing(4)})`,
    paddingBottom: 0,
  },
  getStarted: {
    [theme.breakpoints.up('md')]: {
      marginTop: theme.spacing(3),
      width: 'fit-content'
    }
  },
  link: {
    color: 'inherit'
  },
  preventionContainer: {
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2),
    marginTop: 'auto',
    marginLeft: -theme.spacing(2),
    marginRight: -theme.spacing(2),
  },
  bookSession: {
    maxWidth: '316px',
    maxHeight: '156px',
    padding: theme.spacing(2, 3, 3, 3),
    gap: theme.spacing(1),
    backgroundColor: theme.palette.secondary.background,
  },
  bookSessionTitle: {
    fontSize: '12px',
    fontWeight: 700,
    textTransform: 'uppercase',
  },
  steps: {
    display: 'flex',
    gap: theme.spacing(1.5),
    placeItems: 'baseline',
    '& > p': {
      fontSize: '14px'
    }
  },
  stepNumber: {
    fontSize: '14px',
    fontWeight: 600,
    color: theme.palette.secondary.main,
  },
}));
